import { pipToDecimal } from '@katanaperps/katana-perps-sdk';

import { useWalletWithPositionsQuery } from 'api/queries/walletPositionsQueries';
import { formatPercentage } from 'lib/display/formatting';
import { useSelector } from 'reduxState/storeCore';

import { ApiVault } from './types';

import type { RemainingTime } from 'lib/date';

export const isVaultManager = (walletAddress: string, vault: ApiVault) =>
  !!walletAddress &&
  vault.manager.toLowerCase() === walletAddress.toLowerCase();

export const useOwnVaultQuery = (
  enabled = true
): { name?: string; manager: string } | false | undefined => {
  const walletAddress = useSelector(state => state.wallet.walletAddress);
  const { data: walletData } = useWalletWithPositionsQuery(enabled);
  if (!walletData) {
    return undefined;
  }
  return walletData?.managedAccountProvider
    ? {
        name: walletData.vaultName,
        manager: walletAddress,
      }
    : false;
  // const walletManagedVault = useMemo(() => {
  //   return !vaultsResponse
  //     ? undefined
  //     : vaultsResponse.vaults.find(vault =>
  //         isVaultManager(walletAddress, vault)
  //       ) || false;
  // }, []);
  // return walletManagedVault;
};

export const getVaultTypeLabel = (vaultType: ApiVault['type'] | undefined) =>
  vaultType === 'fixedIncomeVaultV1'
    ? 'Fixed Income v1'
    : vaultType === 'profitShareVaultV1'
    ? 'Profit Share Vault V1'
    : vaultType || '';

export const formatVaultCollateralization = (
  collateralizationFromAPI?: string
) => {
  if (!collateralizationFromAPI) {
    return '';
  }
  const collateralizationNum = Number(collateralizationFromAPI) * 100;
  return collateralizationNum > 1000
    ? '1000+%'
    : `${formatPercentage(collateralizationNum)}%`;
};

export function calculateVaultTresholds(
  configFieldsTransformed:
    | {
        minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal: string;
        maximumTotalOwedQuantityAvailableMultiplierToInitiateExit: string;
      }
    | undefined,
  summaryData: {
    totalOwedQuantity: bigint | undefined;
  }
) {
  const totalOwedQuantity =
    summaryData?.totalOwedQuantity !== undefined
      ? pipToDecimal(summaryData.totalOwedQuantity)
      : undefined;
  const minMultiplier =
    configFieldsTransformed?.minimumTotalOwedQuantityAvailableMultiplierToAllowManagerWalletWithdrawal;
  const maxMultiplier =
    configFieldsTransformed?.maximumTotalOwedQuantityAvailableMultiplierToInitiateExit;
  const withdrawalThresholdValue =
    totalOwedQuantity !== undefined && minMultiplier !== undefined
      ? Number(totalOwedQuantity) * Number(minMultiplier)
      : undefined;
  const exitThresholdValue =
    totalOwedQuantity !== undefined && maxMultiplier !== undefined
      ? Number(totalOwedQuantity) * Number(maxMultiplier)
      : undefined;
  return {
    exitThresholdValue,
    withdrawalThresholdValue,
  };
}

export const vaultCreatedUrl = (vaultManager: string) =>
  `/vaults/${vaultManager}#vaultCreated`;

/**
 * Determines the label type and text based on time difference
 * Used for displaying withdrawal processing time or age of withdrawal requests
 */
export function getVaultWithdrawalTimeDiffLabel(diff: RemainingTime): {
  type: 'error' | 'warning' | 'success';
  text: string;
} | null {
  if (diff.days > 0) {
    return {
      type: 'error',
      text: `${diff.days} ${diff.days === 1 ? 'day' : 'days'}`,
    };
  }
  if (diff.hours > 0) {
    return {
      type: 'error',
      text: `${diff.hours} ${diff.hours === 1 ? 'hour' : 'hours'}`,
    };
  }
  if (diff.minutes > 0) {
    return {
      type: diff.minutes > 5 ? 'warning' : 'success',
      text: `${diff.minutes} min`,
    };
  }
  // Less than a minute
  return {
    type: 'success',
    text: '< 1 min',
  };
}
